'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/db/supabase';
import type { NutritionGoals } from '@/types/nutrition';

interface NutritionContextType {
  goals: NutritionGoals | null;
  loading: boolean;
  setGoals: (goals: NutritionGoals) => Promise<void>;
}

const NutritionContext = createContext<NutritionContextType | undefined>(undefined);

export function NutritionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [goals, setGoalsState] = useState<NutritionGoals | null>(null);
  const [loading, setLoading] = useState(true);

  // Carregar metas nutricionais do usuário
  useEffect(() => {
    async function loadGoals() {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('nutrition_goals')
          .select('*')
          .eq('user_id', user.id);

        if (error) throw error;

        if (data && data.length > 0) {
          // Converter metas individuais para o formato NutritionGoals
          const nutritionGoals: NutritionGoals = {
            targetWeight: 0,
            dailyCalories: 0,
            protein: 0,
            carbs: 0,
            fats: 0
          };

          data.forEach(goal => {
            switch (goal.type) {
              case 'weight':
                nutritionGoals.targetWeight = goal.target_value;
                break;
              case 'calories':
                nutritionGoals.dailyCalories = goal.target_value;
                break;
              case 'protein':
                nutritionGoals.protein = goal.target_value;
                break;
              case 'carbs':
                nutritionGoals.carbs = goal.target_value;
                break;
              case 'fats':
                nutritionGoals.fats = goal.target_value;
                break;
            }
          });

          setGoalsState(nutritionGoals);
        }
      } catch (error) {
        console.error('Erro ao carregar metas:', error);
      } finally {
        setLoading(false);
      }
    }

    loadGoals();
  }, [user]);

  const setGoals = async (newGoals: NutritionGoals) => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Deletar metas antigas primeiro
      await supabase
        .from('nutrition_goals')
        .delete()
        .eq('user_id', user.id);

      // Inserir novas metas como registros individuais
      const goals = [
        {
          user_id: user.id,
          type: 'weight' as const,
          target_value: newGoals.targetWeight,
          current_value: newGoals.targetWeight,
          frequency: 'weekly' as const,
          start_date: new Date().toISOString(),
          end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          user_id: user.id,
          type: 'calories' as const,
          target_value: newGoals.dailyCalories,
          current_value: 0,
          frequency: 'daily' as const,
          start_date: new Date().toISOString(),
          end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          user_id: user.id,
          type: 'protein' as const,
          target_value: newGoals.protein,
          current_value: 0,
          frequency: 'daily' as const,
          start_date: new Date().toISOString(),
          end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          user_id: user.id,
          type: 'carbs' as const,
          target_value: newGoals.carbs,
          current_value: 0,
          frequency: 'daily' as const,
          start_date: new Date().toISOString(),
          end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          user_id: user.id,
          type: 'fats' as const,
          target_value: newGoals.fats,
          current_value: 0,
          frequency: 'daily' as const,
          start_date: new Date().toISOString(),
          end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        }
      ];

      const { error } = await supabase
        .from('nutrition_goals')
        .insert(goals);

      if (error) throw error;

      setGoalsState(newGoals);
    } catch (error) {
      console.error('Erro ao atualizar metas:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <NutritionContext.Provider
      value={{
        goals,
        loading,
        setGoals,
      }}
    >
      {children}
    </NutritionContext.Provider>
  );
}

export function useNutrition() {
  const context = useContext(NutritionContext);
  if (context === undefined) {
    throw new Error('useNutrition must be used within a NutritionProvider');
  }
  return context;
}